// Dispatch board — the LAST step of an order.
// Won + payment confirmed → hand over the goods → dispatch here.
// A mixed order (a ready CAP part + OSM parts still being sourced) is dispatched
// PART BY PART. The order becomes 'completed' only once every part is out.
// Shared by the superadmin panel and the ops-admin workspace (api prop).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiMapPin,
  FiPackage,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiTrash2,
  FiTruck,
} from 'react-icons/fi';
import { Alert, Button, DeleteConfirm } from '../common/index.js';
import { deleteOrder } from '../../api/deletionsApi';
import { useOpsDay } from '../../hooks/useOpsDay';
import { PAYMENT_MODES } from '../../constants/paymentModes';

const formatPrice = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const shortDate = (value) => String(value || '').split(' at ')[0];

const TABS = [
  { value: 'ready', label: 'Ready to dispatch' },
  { value: 'awaiting_payment', label: 'Awaiting payment' },
  { value: 'dispatched', label: 'Dispatched' },
];

export const ModePicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {PAYMENT_MODES.map((m) => (
      <button
        key={m.v}
        type="button"
        onClick={() => onChange(m.v)}
        className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-all ${value === m.v ? 'border-violet-500 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
      >
        {m.l}
      </button>
    ))}
  </div>
);

// Collect / confirm the payment for a won order — FULL or ADVANCE and how it
// was paid. On a full payment the PRICE is editable: the money actually taken
// often lands ₹500–1000 off the quote, and a changed price is recorded as the
// deal's FINAL rate. Exported: the Payments board reuses it.
export const PaymentModal = ({ order, submitting, error, onClose, onConfirm }) => {
  const reported = order.payment;   // sales/supervisor may have reported one already
  const quoted = Number(reported?.total ?? order.total) || 0;
  const [ptype, setPtype] = useState(reported?.type === 'advance' ? 'advance' : 'full');
  const [mode, setMode] = useState(reported?.mode || 'cash');
  const [advance, setAdvance] = useState(
    reported?.type === 'advance' && reported?.advance_amount
      ? String(Number(reported.advance_amount)) : ''
  );
  const [price, setPrice] = useState(
    reported?.type === 'final' && reported?.final_amount
      ? String(Number(reported.final_amount)) : (quoted ? String(quoted) : '')
  );
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState('');
  const priceNum = Number(price);
  const priceChanged = Number.isFinite(priceNum) && priceNum > 0 && priceNum !== quoted;
  // An advance is checked against the quote — price corrections happen on the
  // full/final payment, not while money is still owed.
  const total = quoted;

  const submit = () => {
    setLocalError('');
    if (ptype === 'advance') {
      const adv = Number(advance);
      if (!Number.isFinite(adv) || adv <= 0) { setLocalError('Enter the advance amount'); return; }
      if (adv >= total && total > 0) { setLocalError('An advance must be less than the total — use Full payment instead'); return; }
      onConfirm({ payment_mode: mode, payment_type: 'advance', advance_amount: String(advance) });
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum <= 0) { setLocalError('Enter the final price collected'); return; }
    if (priceChanged) {
      if (!reason.trim()) { setLocalError('Say why the price changed — the superadmin sees this note'); return; }
      onConfirm({
        payment_mode: mode,
        payment_type: 'final',
        final_amount: String(priceNum),
        reason: reason.trim(),
      });
    } else {
      onConfirm({ payment_mode: mode, payment_type: 'full' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={submitting ? undefined : onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900">Collect payment</h3>
        <p className="mt-0.5 text-sm text-slate-500">{order.customer_name} · {order.car}</p>

        {(error || localError) && <div className="mt-3"><Alert type="error" message={error || localError} /></div>}

        <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-slate-500">
              {ptype === 'full' ? 'Price collected' : 'Order total'}
            </span>
            {ptype === 'full' ? (
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">₹</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="numeric"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder={quoted ? String(quoted) : 'Amount'}
                  className="w-36 rounded-xl border border-slate-200 bg-white py-2 pl-7 pr-3 text-right text-base font-black text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
            ) : (
              <span className="text-lg font-black text-slate-900">{formatPrice(total) || '—'}</span>
            )}
          </div>
          {ptype === 'full' && priceChanged && quoted > 0 && (
            <p className="mt-1.5 text-right text-xs font-bold text-violet-600">
              Final rate — {priceNum > quoted ? '+' : '−'}{formatPrice(Math.abs(priceNum - quoted))} vs quote {formatPrice(quoted)}
            </p>
          )}
        </div>

        {/* The superadmin never sees the correction happen — only this note */}
        {ptype === 'full' && priceChanged && (
          <div className="mt-3">
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Why did the price change? *
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. customer bargained ₹500 at the counter"
              className="w-full resize-none rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
            <p className="mt-1 text-xs text-slate-400">Goes to the superadmin's price-change record.</p>
          </div>
        )}

        {reported && !reported.confirmed && (
          <p className="mt-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
            Reported by {reported.reported_by === 'sales' ? 'the salesperson' : reported.reported_by}:{' '}
            <span className="font-bold">
              {reported.type === 'advance'
                ? `advance ${formatPrice(reported.advance_amount)}` : reported.type}
              {reported.mode_label ? ` · ${reported.mode_label}` : ''}
            </span>
            {' — confirm or correct it below.'}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          {[{ v: 'full', l: 'Full payment' }, { v: 'advance', l: 'Advance' }].map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setPtype(t.v)}
              className={`rounded-xl border-2 px-3 py-3 text-sm font-bold transition-all ${ptype === t.v ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {ptype === 'advance' && (
          <div className="mt-3">
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Advance collected *</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input
                type="number"
                min="0"
                step="any"
                inputMode="numeric"
                value={advance}
                onChange={(e) => setAdvance(e.target.value)}
                placeholder="Amount received now"
                className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-4 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>
            {Number(advance) > 0 && Number(advance) < total && (
              <p className="mt-1.5 text-xs font-semibold text-amber-600">
                Balance {formatPrice(total - Number(advance))} to collect later
              </p>
            )}
          </div>
        )}

        <p className="mt-4 mb-1.5 text-sm font-semibold text-slate-700">Paid by</p>
        <ModePicker value={mode} onChange={setMode} />

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" loading={submitting} onClick={submit} className="gap-2">
            <FiCreditCard className="h-4 w-4" /> Confirm payment
          </Button>
        </div>
      </div>
    </div>
  );
};

// Collect the open balance of an advance deal. Exported for the Payments board.
export const BalanceModal = ({ order, submitting, error, onClose, onConfirm }) => {
  const [mode, setMode] = useState('cash');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={submitting ? undefined : onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900">Collect balance</h3>
        <p className="mt-0.5 text-sm text-slate-500">{order.customer_name} · {order.car}</p>
        {error && <div className="mt-3"><Alert type="error" message={error} /></div>}
        <div className="mt-4 flex items-center justify-between rounded-xl bg-rose-50 px-4 py-3">
          <span className="text-sm font-semibold text-rose-600">Balance due</span>
          <span className="text-lg font-black text-rose-600">{formatPrice(order.payment?.balance_due) || '—'}</span>
        </div>
        <p className="mt-4 mb-1.5 text-sm font-semibold text-slate-700">Paid by</p>
        <ModePicker value={mode} onChange={setMode} />
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" loading={submitting} onClick={() => onConfirm(mode)} className="gap-2">
            <FiCreditCard className="h-4 w-4" /> Balance received
          </Button>
        </div>
      </div>
    </div>
  );
};

const SourceTag = ({ source }) => {
  if (source !== 'cap' && source !== 'osm') return null;
  const cap = source === 'cap';
  return (
    <span className={`rounded px-1 py-px text-[9px] font-bold uppercase ${cap ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>
      {source}
    </span>
  );
};

// Supervisor's delivery proof for one part — pack photo(s) + courier receipt,
// each clickable to zoom. onZoom(url) opens the shared lightbox.
const ProofThumbs = ({ part, onZoom }) => {
  const packs = part.pack_image_urls || [];
  if (packs.length === 0 && !part.courier_receipt_url) return null;
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {packs.map((url, i) => (
        <button key={url + i} type="button" onClick={stop(() => onZoom(url))} title={`Pack photo ${i + 1}`} className="relative">
          <img src={url} alt="pack" className="h-10 w-10 rounded-md border-2 border-amber-300 object-cover" />
          <span className="absolute bottom-0 left-0 rounded-tr rounded-bl-md bg-slate-900/70 px-1 text-[7px] font-bold uppercase text-white">Pack</span>
        </button>
      ))}
      {part.courier_receipt_url && (
        <button type="button" onClick={stop(() => onZoom(part.courier_receipt_url))} title="Courier receipt" className="relative">
          <img src={part.courier_receipt_url} alt="receipt" className="h-10 w-10 rounded-md border-2 border-emerald-300 object-cover" />
          <span className="absolute bottom-0 left-0 rounded-tr rounded-bl-md bg-emerald-600/85 px-1 text-[7px] font-bold uppercase text-white">Rcpt</span>
        </button>
      )}
    </div>
  );
};

// Full-screen image lightbox for a proof photo.
const ProofLightbox = ({ url, onClose }) =>
  !url ? null : (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <img src={url} alt="Delivery proof" className="max-h-[85vh] max-w-full rounded-lg" />
    </div>
  );

// Pick which pending parts to hand over now. OSM parts still being sourced can
// be left unchecked and dispatched later.
const DispatchModal = ({ order, submitting, error, onClose, onConfirm }) => {
  const parts = order.parts || [];
  const pending = parts.filter((p) => p.sellable && !p.dispatched);
  const done = parts.filter((p) => p.sellable && p.dispatched);
  // Once the delivery flow is in use for this order (any part handed to the
  // courier), only courier-handed parts are dispatchable here — parts not yet
  // packed & shipped are shown separately and CANNOT be selected, so admin
  // never dispatches an item the supervisor hasn't actually handed over. If NO
  // part went through the courier flow (older / direct counter handover), every
  // pending part stays selectable so that flow still works.
  const anyShipped = pending.some((p) => p.delivery_stage === 'shipped');
  const ready = anyShipped ? pending.filter((p) => p.delivery_stage === 'shipped') : pending;
  const notReady = anyShipped ? pending.filter((p) => p.delivery_stage !== 'shipped') : [];
  const [selected, setSelected] = useState(() => new Set(ready.map((p) => p.id)));
  const [note, setNote] = useState('');
  const [address, setAddress] = useState(order.dispatch_address || '');
  const [zoom, setZoom] = useState(null);   // proof photo lightbox

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = selected.size === ready.length && ready.length > 0;
  const willComplete = done.length + selected.size >= parts.filter((p) => p.sellable).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={submitting ? undefined : onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900">Dispatch parts</h3>
        <p className="mt-0.5 text-sm text-slate-500">{order.customer_name} · {order.car}</p>

        {error && <div className="mt-3"><Alert type="error" message={error} /></div>}

        {/* Delivery address — captured by sales at confirm; editable here in case
            it's missing or needs a fix before the parts go out. */}
        <label htmlFor="dispatch-address" className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <FiMapPin className="h-4 w-4 text-rose-400" /> Delivery address
        </label>
        <textarea
          id="dispatch-address"
          rows={2}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Where the parts are being delivered"
          className="mt-1.5 w-full resize-y rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
        />

        {order.balance_open && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
            <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p className="text-xs text-amber-800">
              <span className="font-bold">Balance {formatPrice(order.payment?.balance_due)} still due.</span>{' '}
              Collect it before handing over, or dispatch anyway if agreed.
            </p>
          </div>
        )}

        {/* Already handed over */}
        {done.length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Already dispatched</p>
            <div className="flex flex-wrap gap-1.5">
              {done.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  <FiCheckCircle className="h-3 w-3" /> {p.name} <SourceTag source={p.source} />
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Pick what to send now — only parts handed to the courier */}
        <div className="mt-4">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Hand over now</p>
            {ready.length > 1 && (
              <button
                type="button"
                onClick={() => setSelected(allSelected ? new Set() : new Set(ready.map((p) => p.id)))}
                className="text-[11px] font-semibold text-violet-600 hover:text-violet-700"
              >
                {allSelected ? 'Clear all' : 'Select all'}
              </button>
            )}
          </div>
          {ready.length > 0 ? (
            <div className="space-y-1.5">
              {ready.map((p) => {
                const on = selected.has(p.id);
                return (
                  <label
                    key={p.id}
                    className={`flex cursor-pointer flex-col gap-2 rounded-xl border px-3 py-2.5 transition-all ${on ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input type="checkbox" checked={on} onChange={() => toggle(p.id)} className="h-4 w-4 accent-violet-600" />
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700">
                        {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
                      </span>
                      <SourceTag source={p.source} />
                      <span className="text-xs font-semibold text-slate-500">{formatPrice(p.price)}</span>
                    </div>
                    <ProofThumbs part={p} onZoom={setZoom} />
                  </label>
                );
              })}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-400">
              No parts have been handed to the courier yet.
            </p>
          )}
          <p className="mt-2 text-[11px] text-slate-400">
            {willComplete
              ? 'This dispatches everything — the order will be marked completed.'
              : 'The rest can be dispatched later, once handed to the courier.'}
          </p>
        </div>

        {/* Not handed to the courier yet — can't be dispatched from here. The
            supervisor must pack & hand these over first (e.g. an OSM part still
            being sourced). Shown so admin knows why they're not selectable. */}
        {notReady.length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-500">
              Not handed to courier yet — can’t dispatch
            </p>
            <div className="space-y-1.5">
              {notReady.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2.5 rounded-xl border border-dashed border-amber-200 bg-amber-50/60 px-3 py-2.5"
                  title="The supervisor must pack and hand this to the courier before it can be dispatched"
                >
                  <FiClock className="h-4 w-4 shrink-0 text-amber-500" />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-600">
                    {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
                  </span>
                  <SourceTag source={p.source} />
                  <span className="rounded-full border border-amber-200 bg-white px-2 py-0.5 text-[10px] font-bold text-amber-600">
                    {p.delivery_stage === 'packed' ? 'Packed, not shipped' : 'Not packed'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <label htmlFor="dispatch-note" className="mt-4 block text-sm font-semibold text-slate-700">Note (optional)</label>
        <input
          id="dispatch-note"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. handed over at counter / sent by bus"
          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
        />

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={selected.size === 0}
            onClick={() => onConfirm([...selected], note, address)}
            className="gap-2"
          >
            <FiTruck className="h-4 w-4" /> Dispatch {selected.size > 0 ? selected.size : ''}
          </Button>
        </div>

        <ProofLightbox url={zoom} onClose={() => setZoom(null)} />
      </div>
    </div>
  );
};

const OrderCard = ({ order, onDispatch, onUndo, onCollectPayment, onApprove, approveBusy, onCollectBalance, onDelete }) => {
  const ready = order.bucket === 'ready';
  const awaiting = order.bucket === 'awaiting_payment';
  const dispatched = order.bucket === 'dispatched';
  const prog = order.dispatch_progress || { dispatched: 0, total: 0, partial: false, pending_parts: [] };
  const partial = prog.partial;
  const parts = order.parts || [];
  // One-tap approve = confirm the reported payment + dispatch the parts the
  // supervisor already handed to the courier. Needs both to exist.
  const canApprove = awaiting && order.payment && !order.payment.confirmed
    && parts.some((p) => p.sellable && !p.dispatched && p.delivery_stage === 'shipped');
  // The supervisor's pack photos + courier receipt belong ON the card: the
  // dispatch modal only ever showed them for parts still to hand over, so once
  // an order was dispatched its proof became unreachable.
  const [zoom, setZoom] = useState(null);
  const proofParts = parts.filter(
    (p) => (p.pack_image_urls?.length || 0) > 0 || p.courier_receipt_url,
  );

  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${partial ? 'border-amber-300' : ready ? 'border-violet-200' : dispatched ? 'border-slate-200' : 'border-amber-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${dispatched ? 'bg-emerald-50 text-emerald-600' : partial ? 'bg-amber-50 text-amber-600' : ready ? 'bg-violet-50 text-violet-600' : 'bg-amber-50 text-amber-600'}`}>
            {dispatched ? <FiCheckCircle className="h-5 w-5" /> : partial ? <FiPackage className="h-5 w-5" /> : <FiTruck className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-slate-900">{order.customer_name || 'Customer'}</h3>
            <p className="mt-0.5 text-sm text-slate-500">{order.car}</p>
            <p className="mt-0.5 text-xs font-medium text-slate-400">
              {formatPrice(order.payment?.total ?? order.total) || '—'} · by {order.salesperson_name || '-'} · won {shortDate(order.won_at)}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {dispatched ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
              <FiCheckCircle className="h-3.5 w-3.5" /> Dispatched {shortDate(order.dispatched_at)}
            </span>
          ) : partial ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
              <FiPackage className="h-3.5 w-3.5" /> {prog.dispatched}/{prog.total} dispatched
            </span>
          ) : ready ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700">
              <FiCheckCircle className="h-3.5 w-3.5" /> Payment confirmed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
              <FiClock className="h-3.5 w-3.5" /> Payment not confirmed
            </span>
          )}
          {order.balance_open && !dispatched && (
            <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600">
              <FiCreditCard className="h-3.5 w-3.5" /> Balance {formatPrice(order.payment?.balance_due)} due
            </span>
          )}
          {ready && (
            <Button size="sm" variant="primary" onClick={() => onDispatch(order)} className="gap-1.5">
              <FiTruck className="h-3.5 w-3.5" /> {partial ? `Dispatch remaining (${prog.total - prog.dispatched})` : 'Dispatch'}
            </Button>
          )}
          {awaiting && (
            <div className="flex flex-wrap justify-end gap-1.5">
              {canApprove && (
                <Button size="sm" variant="success" loading={approveBusy} onClick={() => onApprove(order)} className="gap-1.5">
                  <FiCheckCircle className="h-3.5 w-3.5" /> Approve &amp; dispatch
                </Button>
              )}
              <Button size="sm" variant={canApprove ? 'secondary' : 'primary'} onClick={() => onCollectPayment(order)} className="gap-1.5">
                <FiCreditCard className="h-3.5 w-3.5" /> {order.payment ? 'Confirm payment' : 'Collect payment'}
              </Button>
            </div>
          )}
          {order.balance_open && !awaiting && (
            <Button size="sm" variant="secondary" onClick={() => onCollectBalance(order)} className="gap-1.5">
              <FiCreditCard className="h-3.5 w-3.5" /> Collect balance
            </Button>
          )}
          {(dispatched || partial) && (
            <button
              type="button"
              onClick={() => onUndo(order)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-rose-500"
            >
              <FiRotateCcw className="h-3 w-3" /> Undo all
            </button>
          )}
          {/* SUPERADMIN only (prop-gated — this board is shared with the ops login) */}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(order)}
              title="Delete this order (kept in Deleted Records)"
              className="rounded-lg border border-slate-200 p-2 text-slate-300 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
            >
              <FiTrash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Deliver-to address — captured by sales at confirm, shown to the
          dispatcher. Missing on older orders; admin can add it in the modal. */}
      {order.dispatch_address ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50/60 px-3 py-2.5">
          <FiMapPin className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-400">Deliver to</p>
            <p className="text-sm font-medium text-slate-700">{order.dispatch_address}</p>
          </div>
        </div>
      ) : (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
          <FiMapPin className="h-3.5 w-3.5" /> No delivery address — add it when dispatching
        </p>
      )}

      {/* Parts — dispatched ones ticked, pending ones flagged by source */}
      {parts.length > 0 && (
        <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Parts</p>
            {partial && prog.pending_parts?.length > 0 && (
              <p className="text-[11px] font-semibold text-amber-600">Waiting: {prog.pending_parts.join(', ')}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {parts.filter((p) => p.sellable).map((p) => (
              <span
                key={p.id}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${p.dispatched ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}
              >
                {p.dispatched ? <FiCheckCircle className="h-3 w-3" /> : <FiClock className="h-3 w-3 text-slate-300" />}
                {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
                <SourceTag source={p.source} />
              </span>
            ))}
            {/* Returned/cancelled/exchanged parts — not dispatched, shown so the
                team knows why they're not in the hand-over set. A cash refund on
                a part never dispatched reads as CANCELLED. */}
            {parts.filter((p) => p.returned).map((p) => {
              const label = p.return_state === 'cancelled' ? 'Cancelled'
                : p.return_state === 'exchanged' ? 'Exchanged' : 'Returned';
              return (
                <span
                  key={p.id}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600 line-through"
                  title={`${label} — not dispatched`}
                >
                  <FiRotateCcw className="h-3 w-3" />
                  {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
                  <SourceTag source={p.source} />
                  <span className="rounded bg-rose-100 px-1 py-px text-[9px] font-bold uppercase no-underline">{label}</span>
                </span>
              );
            })}
          </div>

          {/* What the supervisor actually handed over — tap any photo to zoom */}
          {proofParts.length > 0 && (
            <div className="mt-2.5 border-t border-slate-200/70 pt-2.5">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Delivery proof
              </p>
              <div className="space-y-1.5">
                {proofParts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-slate-600">{p.name}</p>
                      {p.courier_name && (
                        <p className="truncate text-[11px] text-slate-400">via {p.courier_name}</p>
                      )}
                    </div>
                    <ProofThumbs part={p} onZoom={setZoom} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ProofLightbox url={zoom} onClose={() => setZoom(null)} />

      {dispatched && (order.dispatch_note || order.dispatched_by) && (
        <p className="mt-2 text-xs text-slate-400">
          {order.dispatched_by ? `By ${order.dispatched_by}` : ''}{order.dispatch_note ? ` · ${order.dispatch_note}` : ''}
        </p>
      )}
    </div>
  );
};

// `canDelete` — passed ONLY from the superadmin DispatchPage; the ops-admin
// twin renders this same board without it, so the ops login never sees delete.
const DispatchBoard = ({ api, canDelete = false }) => {
  const { refresh: refreshOpsDay } = useOpsDay();
  const [tab, setTab] = useState('ready');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(null);      // order being dispatched
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [day, setDay] = useState(null);            // today banner numbers
  const [payTarget, setPayTarget] = useState(null);
  const [balanceTarget, setBalanceTarget] = useState(null);
  const [approveBusyId, setApproveBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.list({ status: tab, q });
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load dispatch list');
    } finally {
      setLoading(false);
    }
  }, [api, tab, q]);

  const loadDay = useCallback(async () => {
    // Also nudges the shared ops feed, so the nav badges can't sit stale
    // showing work the admin just cleared. (No-op on the superadmin route,
    // which renders this board outside the ops layout.)
    refreshOpsDay();
    try {
      const res = await api.day();
      setDay(res.data ?? res);
    } catch (_) {}   // banner is additive — the board still works without it
  }, [api, refreshOpsDay]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDay(); }, [loadDay]);

  const confirmDispatch = async (partIds, note, address) => {
    setSubmitting(true);
    setModalError('');
    try {
      const body = { dispatched: true, part_ids: partIds, note };
      // Persist any address the admin added/edited in the modal
      if (address != null) body.dispatch_address = address;
      await api.set(target.inquiry_id, body);
      setTarget(null);
      load();
      loadDay();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Dispatch failed');
    } finally {
      setSubmitting(false);
    }
  };

  const undoDispatch = async (order) => {
    try {
      await api.set(order.inquiry_id, { dispatched: false });
      load();
      loadDay();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Undo failed');
    }
  };

  const confirmPayment = async (payload) => {
    setSubmitting(true);
    setModalError('');
    try {
      await api.setPayment(payTarget.inquiry_id, payload);
      setPayTarget(null);
      load();
      loadDay();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not record the payment');
    } finally {
      setSubmitting(false);
    }
  };

  const approveOrder = async (order) => {
    setApproveBusyId(order.inquiry_id);
    setError('');
    try {
      await api.approve(order.inquiry_id);
      load();
      loadDay();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Approve failed');
    } finally {
      setApproveBusyId(null);
    }
  };

  const collectBalance = async (mode) => {
    setSubmitting(true);
    setModalError('');
    try {
      await api.collectBalance(balanceTarget.inquiry_id, { paid: true, mode });
      setBalanceTarget(null);
      load();
      loadDay();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not record the balance');
    } finally {
      setSubmitting(false);
    }
  };

  const counts = useMemo(() => ({
    ready: data?.ready_count ?? 0,
    awaiting_payment: data?.awaiting_payment_count ?? 0,
    dispatched: data?.dispatched_count ?? 0,
  }), [data]);
  const orders = data?.orders ?? [];

  return (
    <div className="space-y-4">
      {error && <Alert type="error" message={error} />}

      {/* Today at a glance — what already went out, incl. one-tap approvals */}
      {day?.dispatch && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-2.5 text-sm">
          <span className="inline-flex items-center gap-1.5 font-bold text-emerald-800">
            <FiTruck className="h-4 w-4" />
            {day.dispatch.today_count} order{day.dispatch.today_count === 1 ? '' : 's'} dispatched today
          </span>
          <span className="font-bold text-emerald-700">{formatPrice(day.dispatch.today_value)}</span>
          {day.dispatch.orders.length > 0 && (
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-emerald-700/80">
              {day.dispatch.orders.slice(0, 3).map((o) => o.customer_name).filter(Boolean).join(' · ')}
              {day.dispatch.orders.length > 3 ? ` +${day.dispatch.orders.length - 3} more` : ''}
            </span>
          )}
        </div>
      )}

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = tab === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
              >
                {t.label}
                <span className={`rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                  {counts[t.value]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Customer, car or part…"
              className="w-64 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
          <button
            type="button"
            onClick={() => { load(); loadDay(); }}
            disabled={loading}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50"
            aria-label="Refresh"
            title="Refresh"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
          <FiTruck className="mx-auto mb-2 h-8 w-8 text-slate-200" />
          <p className="text-sm text-slate-400">
            {tab === 'ready' ? 'Nothing waiting to dispatch' : tab === 'awaiting_payment' ? 'No orders awaiting payment' : 'Nothing dispatched yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <OrderCard
              key={order.inquiry_id}
              order={order}
              onDispatch={(o) => { setModalError(''); setTarget(o); }}
              onUndo={undoDispatch}
              onCollectPayment={(o) => { setModalError(''); setPayTarget(o); }}
              onApprove={approveOrder}
              approveBusy={approveBusyId === order.inquiry_id}
              onCollectBalance={(o) => { setModalError(''); setBalanceTarget(o); }}
              onDelete={canDelete ? (o) => { setDeleteError(''); setDeleteTarget(o); } : undefined}
            />
          ))}
        </div>
      )}

      {target && (
        <DispatchModal
          order={target}
          submitting={submitting}
          error={modalError}
          onClose={() => { setTarget(null); setModalError(''); }}
          onConfirm={confirmDispatch}
        />
      )}
      {payTarget && (
        <PaymentModal
          order={payTarget}
          submitting={submitting}
          error={modalError}
          onClose={() => { setPayTarget(null); setModalError(''); }}
          onConfirm={confirmPayment}
        />
      )}
      {balanceTarget && (
        <BalanceModal
          order={balanceTarget}
          submitting={submitting}
          error={modalError}
          onClose={() => { setBalanceTarget(null); setModalError(''); }}
          onConfirm={collectBalance}
        />
      )}
      {deleteTarget && (
        <DeleteConfirm
          title={`Delete order #${deleteTarget.inquiry_id}?`}
          summary={`${deleteTarget.customer_name || 'Customer'} · ${deleteTarget.car || ''}`}
          consequences={[
            'The WHOLE order goes — its parts, chat, follow-ups and returns with it.',
            'Any stock-recovery entries its dispatch created are reversed.',
            'This cannot be undone (the snapshot in Deleted Records is read-only).',
          ]}
          requirePassword
          busy={deleteBusy}
          error={deleteError}
          onClose={() => { if (!deleteBusy) { setDeleteTarget(null); setDeleteError(''); } }}
          onConfirm={async ({ password, reason }) => {
            setDeleteBusy(true);
            setDeleteError('');
            try {
              await deleteOrder(deleteTarget.inquiry_id, { password, reason, section: 'dispatch' });
              setDeleteTarget(null);
              await load();
            } catch (e) {
              setDeleteError(e?.response?.data?.error || e.message || 'Could not delete the order');
            } finally {
              setDeleteBusy(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default DispatchBoard;
